import { useMemo, useState } from "react";
import type { CatalogPrice } from "@ferrivia/api-contract";
import { useSession } from "@ferrivia/auth-web";
import { useFormatters, useTranslation } from "@ferrivia/i18n";
import { Button, Sheet, SheetContent, SheetTitle, Text, ToggleChip } from "@ferrivia/ui";
import { useAppContext } from "../../app/useAppContext";
import { useCatalog } from "../../hooks/useCatalog";
import { useDismissOnBack } from "../../hooks/useDismissOnBack";
import { IRON_PANEL_SLUG } from "../IronPanelPurchase/IronPanelPurchase";
import { useCheckout } from "../IronPanelPurchase/useCheckout";
import s from "./AnalysisPurchase.module.css";

// The Stripe product kept its old slug through the rename to "alkukartoitus".
export const ANALYSIS_SLUG = "raudanpuutteen-alkukartoitus";

/** The initial assessment purchase, opened from the offer that heads the
    Support page's steps card while unpaid. Labs are an optional add-on line,
    as on the old Store card — the same for every client, never preselected. */
export function AnalysisPurchaseSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation("client");
  const { rootElement } = useAppContext();

  useDismissOnBack(open, () => onOpenChange(false), "analysisPurchase");

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent container={rootElement} aria-describedby={undefined}>
        <SheetTitle>{t("store.products.analysis.name", "Raudanpuutteen alkukartoitus")}</SheetTitle>
        <PurchaseBody />
      </SheetContent>
    </Sheet>
  );
}

// Inside SheetContent, so the catalog is only fetched once the sheet opens.
function PurchaseBody() {
  const { t } = useTranslation("client");
  const { currencyEur } = useFormatters();
  const { trust } = useSession();
  const { state } = useCatalog();
  const [labsSelected, setLabsSelected] = useState(false);
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);

  const priceBySlug = useMemo(() => {
    const map = new Map<string, CatalogPrice>();
    if (state.status === "success") {
      for (const product of state.data.products) {
        const price = product.prices[0];
        if (price) map.set(product.slug, price);
      }
    }
    return map;
  }, [state]);

  const handleOrder = useCheckout(priceBySlug);

  if (state.status === "loading") return null;

  const price = priceBySlug.get(ANALYSIS_SLUG);
  const labsPrice = priceBySlug.get(IRON_PANEL_SLUG);

  if (!price) {
    return (
      <Text as="p" size="sm" className={s.description}>
        {t("support.purchase.unavailable", "Alkukartoitus ei ole juuri nyt saatavilla.")}
      </Text>
    );
  }

  const disabled = trust !== "verified" || pending;
  const totalCents = price.unitAmountCents + (labsSelected && labsPrice ? labsPrice.unitAmountCents : 0);

  async function handleClick() {
    setPending(true);
    setFailed(false);
    const lookupKeys = [price!.lookupKey, ...(labsSelected && labsPrice ? [labsPrice.lookupKey] : [])];
    try {
      await handleOrder(lookupKeys);
    } catch {
      setPending(false);
      setFailed(true);
    }
  }

  return (
    <>
      <Text as="p" size="sm" className={s.description}>
        {t(
          "support.purchase.description",
          "Ferrivian ravitsemusasiantuntija käy läpi elämäntilanteesi, elintapasi, verikokeidesi tulokset, ja laatii suunnitelman raudan imeytymisen ja suoliston hyvinvoinnin tueksi.",
        )}
      </Text>

      <ul className={s.includes}>
        <li className={s.includesItem}>{t("support.purchase.includes.analysis", "Alkukartoitusraportti")}</li>
        <li className={s.includesItem}>{t("support.purchase.includes.call", "Alkukartoituksen henkilökohtainen läpikäyntipuhelu")}</li>
        <li className={s.includesItem}>{t("support.purchase.includes.plan", "Henkilökohtainen suunnitelma")}</li>
      </ul>

      {labsPrice && (
        <ToggleChip
          className={s.addonChip}
          checked={labsSelected}
          disabled={disabled}
          onChange={() => setLabsSelected((previous) => !previous)}
        >
          {t("store.addon.labs", "Lisää laboratoriomittaukset")} · +{currencyEur.format(labsPrice.unitAmountCents / 100)}
        </ToggleChip>
      )}

      <div className={s.footer}>
        <span className={s.total}>
          <span className={s.totalLabel}>{t("store.lab.total", "Yhteensä")}</span>
          <span className={s.totalAmount}>{currencyEur.format(totalCents / 100)}</span>
        </span>
        <Button variant="primary" disabled={disabled} onClick={handleClick}>
          {pending ? t("store.cta.ordering", "Tilataan…") : t("store.cta.order", "Tilaa")}
        </Button>
      </div>

      {failed && (
        <Text as="p" size="sm" className={s.error}>
          {t("store.error", "Kassalle siirtyminen epäonnistui. Yritä uudestaan.")}
        </Text>
      )}
    </>
  );
}
